import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Separator } from '../../components/ui/separator';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  ArrowLeft, Loader2, Briefcase, DollarSign, Clock, Tag, Package,
  Plus, Pencil, Save
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import RecordActions from '../../components/RecordActions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyService = { name: '', description: '', base_price: '', duration_minutes: '120' };
const emptyAddOn = { name: '', price: '', duration_minutes: '30' };

const AdminServicesPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/settings');
  const [services, setServices] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog state
  const [svcDialogOpen, setSvcDialogOpen] = useState(false);
  const [aoDialogOpen, setAoDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingAddOn, setEditingAddOn] = useState(null);
  const [svcForm, setSvcForm] = useState(emptyService);
  const [aoForm, setAoForm] = useState(emptyAddOn);
  const [saving, setSaving] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [svcRes, aoRes] = await Promise.all([
        axios.get(`${API_URL}/api/services?active_only=false`, { headers }),
        axios.get(`${API_URL}/api/add-ons?active_only=false`, { headers }),
      ]);
      setServices(svcRes.data);
      setAddOns(aoRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Service CRUD
  const openCreateService = () => { setEditingService(null); setSvcForm(emptyService); setSvcDialogOpen(true); };
  const openEditService = (s) => {
    setEditingService(s);
    setSvcForm({ name: s.name, description: s.description || '', base_price: String(s.base_price), duration_minutes: String(s.duration_minutes) });
    setSvcDialogOpen(true);
  };
  const saveService = async () => {
    if (!svcForm.name || !svcForm.base_price) { toast.error('Name and price required'); return; }
    setSaving(true);
    try {
      const payload = { name: svcForm.name, description: svcForm.description || null, base_price: parseFloat(svcForm.base_price), duration_minutes: parseInt(svcForm.duration_minutes) };
      if (editingService) {
        await axios.put(`${API_URL}/api/services/${editingService.id}`, payload, { headers });
        toast.success('Service updated');
      } else {
        await axios.post(`${API_URL}/api/services`, payload, { headers });
        toast.success('Service created');
      }
      setSvcDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };
  const toggleServiceActive = async (s) => {
    try {
      await axios.put(`${API_URL}/api/services/${s.id}`, { is_active: !s.is_active }, { headers });
      toast.success(`Service ${s.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  // Add-on CRUD
  const openCreateAddOn = () => { setEditingAddOn(null); setAoForm(emptyAddOn); setAoDialogOpen(true); };
  const openEditAddOn = (a) => {
    setEditingAddOn(a);
    setAoForm({ name: a.name, price: String(a.price), duration_minutes: String(a.duration_minutes) });
    setAoDialogOpen(true);
  };
  const saveAddOn = async () => {
    if (!aoForm.name || !aoForm.price) { toast.error('Name and price required'); return; }
    setSaving(true);
    try {
      const payload = { name: aoForm.name, price: parseFloat(aoForm.price), duration_minutes: parseInt(aoForm.duration_minutes) };
      if (editingAddOn) {
        await axios.put(`${API_URL}/api/add-ons/${editingAddOn.id}`, payload, { headers });
        toast.success('Add-on updated');
      } else {
        await axios.post(`${API_URL}/api/add-ons`, payload, { headers });
        toast.success('Add-on created');
      }
      setAoDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };
  const toggleAddOnActive = async (a) => {
    try {
      await axios.put(`${API_URL}/api/add-ons/${a.id}`, { is_active: !a.is_active }, { headers });
      toast.success(`Add-on ${a.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" /></div>;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-services-page">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center h-16 gap-4">
          <Button variant="ghost" size="icon" onClick={back} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
          <div className="flex-1"><h1 className="text-xl font-heading text-zinc-900">Services & Add-ons</h1><p className="text-sm text-zinc-500">{services.length} services, {addOns.length} add-ons</p></div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Services */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b border-zinc-100 flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading flex items-center gap-2"><Briefcase className="h-5 w-5 text-[#F56F05]" />Services</CardTitle>
            <Button size="sm" className="bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={openCreateService} data-testid="create-service-btn"><Plus className="h-4 w-4 mr-1" />Add Service</Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-zinc-100">
              {services.map((s, i) => (
                <motion.div key={s.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                  className="flex items-center justify-between p-4 hover:bg-zinc-50 group" data-testid={`service-${s.id}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0"><Briefcase className="h-5 w-5 text-[#F56F05]" /></div>
                    <div className="min-w-0">
                      <p className="font-medium text-zinc-900">{s.name}</p>
                      <p className="text-sm text-zinc-500 truncate">{s.description || 'No description'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right mr-2">
                      <p className="font-semibold text-zinc-900">${s.base_price}</p>
                      <p className="text-zinc-400 text-xs flex items-center gap-1 justify-end"><Clock className="h-3 w-3" />{s.duration_minutes}min</p>
                    </div>
                    <Switch checked={s.is_active} onCheckedChange={() => toggleServiceActive(s)} data-testid={`toggle-service-${s.id}`} />
                    <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); openEditService(s); }} data-testid={`edit-service-${s.id}`}>
                      <Pencil className="h-4 w-4 text-zinc-400" />
                    </Button>
                    <RecordActions
                      canEdit={false}
                      canArchive={false}
                      canDelete={true}
                      deleteUrl={`/api/services/${s.id}`}
                      deleteConfirmTitle={`Delete ${s.name}?`}
                      deleteConfirmDescription="Permanently removes this service. If any appointment references it, deletion will be blocked — use the active toggle to deactivate instead."
                      onDeleted={fetchData}
                      headers={headers}
                      testIdPrefix="service"
                      entityId={s.id}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Add-ons */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b border-zinc-100 flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading flex items-center gap-2"><Package className="h-5 w-5 text-purple-500" />Add-ons</CardTitle>
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" onClick={openCreateAddOn} data-testid="create-addon-btn"><Plus className="h-4 w-4 mr-1" />Add Add-on</Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-zinc-100">
              {addOns.map((a, i) => (
                <motion.div key={a.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                  className="flex items-center justify-between p-4 hover:bg-zinc-50 group" data-testid={`addon-${a.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0"><Tag className="h-5 w-5 text-purple-500" /></div>
                    <p className="font-medium text-zinc-900">{a.name}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right mr-2">
                      <p className="font-semibold text-zinc-900">${a.price}</p>
                      <p className="text-zinc-400 text-xs flex items-center gap-1 justify-end"><Clock className="h-3 w-3" />{a.duration_minutes}min</p>
                    </div>
                    <Switch checked={a.is_active} onCheckedChange={() => toggleAddOnActive(a)} data-testid={`toggle-addon-${a.id}`} />
                    <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); openEditAddOn(a); }} data-testid={`edit-addon-${a.id}`}>
                      <Pencil className="h-4 w-4 text-zinc-400" />
                    </Button>
                    <RecordActions
                      canEdit={false}
                      canArchive={false}
                      canDelete={true}
                      deleteUrl={`/api/add-ons/${a.id}`}
                      deleteConfirmTitle={`Delete ${a.name}?`}
                      deleteConfirmDescription="Permanently removes this add-on. If any appointment references it, deletion will be blocked — use the active toggle to deactivate instead."
                      onDeleted={fetchData}
                      headers={headers}
                      testIdPrefix="addon"
                      entityId={a.id}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Service Dialog */}
      <Dialog open={svcDialogOpen} onOpenChange={setSvcDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingService ? 'Edit Service' : 'New Service'}</DialogTitle>
            <DialogDescription>{editingService ? 'Update service details' : 'Create a new cleaning service'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label className="text-sm mb-1.5 block">Name</Label><Input value={svcForm.name} onChange={e => setSvcForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Standard Clean" data-testid="svc-name" /></div>
            <div><Label className="text-sm mb-1.5 block">Description</Label><Input value={svcForm.description} onChange={e => setSvcForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description" data-testid="svc-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm mb-1.5 block">Base Price ($)</Label><Input type="number" min="0" step="0.01" value={svcForm.base_price} onChange={e => setSvcForm(f => ({ ...f, base_price: e.target.value }))} data-testid="svc-price" /></div>
              <div><Label className="text-sm mb-1.5 block">Duration (min)</Label><Input type="number" min="15" step="15" value={svcForm.duration_minutes} onChange={e => setSvcForm(f => ({ ...f, duration_minutes: e.target.value }))} data-testid="svc-duration" /></div>
            </div>
            <Button className="w-full bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={saveService} disabled={saving} data-testid="save-service-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}{editingService ? 'Update' : 'Create'} Service
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add-on Dialog */}
      <Dialog open={aoDialogOpen} onOpenChange={setAoDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingAddOn ? 'Edit Add-on' : 'New Add-on'}</DialogTitle>
            <DialogDescription>{editingAddOn ? 'Update add-on details' : 'Create a new add-on service'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label className="text-sm mb-1.5 block">Name</Label><Input value={aoForm.name} onChange={e => setAoForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Inside Oven" data-testid="ao-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm mb-1.5 block">Price ($)</Label><Input type="number" min="0" step="0.01" value={aoForm.price} onChange={e => setAoForm(f => ({ ...f, price: e.target.value }))} data-testid="ao-price" /></div>
              <div><Label className="text-sm mb-1.5 block">Duration (min)</Label><Input type="number" min="5" step="5" value={aoForm.duration_minutes} onChange={e => setAoForm(f => ({ ...f, duration_minutes: e.target.value }))} data-testid="ao-duration" /></div>
            </div>
            <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" onClick={saveAddOn} disabled={saving} data-testid="save-addon-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}{editingAddOn ? 'Update' : 'Create'} Add-on
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminServicesPage;
